import type {
  CompanyOverview,
  GroupOverview,
  GroupType,
  GroupsListResponse,
  GroupTopUsersResponse,
  CompaniesListResponse,
  CompanyDetail,
  CompanyLink,
  CompanyMember,
  CompanyTopUser,
  CompanyForIdentity,
  ProviderConfigListResponse,
  EnrichResult,
  RevenueTimeseriesPoint,
  UpdateCompanyProfileRequest,
} from '@logspot-platform/platform-types';
import type { ProjectEvent } from '@/types';
import { api } from './api-client';

// The backend company DTO now carries org-curated `links` (detail + profile
// PUT responses); the shared companyDetailSchema hasn't picked the field up
// yet, so widen the type at this service boundary. Optional because older
// response shapes may omit it.
export type CompanyWithLinks = CompanyDetail['company'] & {
  links?: CompanyLink[];
};
export type CompanyDetailWithLinks = Omit<CompanyDetail, 'company'> & {
  company: CompanyWithLinks;
};

// Companies + group-types + enrichment admin API. Same `/api/admin/*` session
// auth + base path as identities-service. Backend wraps payloads in
// SuccessResponse({ data }); we unwrap `.data`.
type SuccessEnvelope<T> = { data: T };

const qs = (params: Record<string, string | number | undefined>): string => {
  const search = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== '') search.set(k, String(v));
  }
  const s = search.toString();
  return s ? `?${s}` : '';
};

export const GroupsService = {
  // ---- Companies ----
  listCompanies: async (
    params: {
      range?: number;
      sort?: 'users' | 'events' | 'last_seen' | 'revenue';
      limit?: number;
      project_id?: string;
      // JSON filter groups from the dashboard.
      filters?: string;
    } = {},
  ): Promise<CompaniesListResponse> => {
    const result = await api.get<SuccessEnvelope<CompaniesListResponse>>(
      `/admin/companies${qs(params)}`,
    );
    return result.data;
  },

  getCompaniesOverview: async (
    params: { range?: number; project_id?: string } = {},
  ): Promise<CompanyOverview> => {
    const result = await api.get<SuccessEnvelope<CompanyOverview>>(
      `/admin/companies/overview${qs(params)}`,
    );
    return result.data;
  },

  // Daily revenue for one company (the company tile's revenue-over-time chart).
  getCompanyRevenueTimeseries: async (
    id: string,
    range?: number,
  ): Promise<{ series: RevenueTimeseriesPoint[] }> => {
    const result = await api.get<
      SuccessEnvelope<{ series: RevenueTimeseriesPoint[] }>
    >(`/admin/companies/${id}/revenue-timeseries${qs({ range })}`);
    return result.data;
  },

  getCompany: async (
    id: string,
    range?: number,
  ): Promise<CompanyDetailWithLinks> => {
    const result = await api.get<SuccessEnvelope<CompanyDetailWithLinks>>(
      `/admin/companies/${id}${qs({ range })}`,
    );
    return result.data;
  },

  getCompanyMembers: async (
    id: string,
    limit?: number,
  ): Promise<{ members: CompanyMember[] }> => {
    const result = await api.get<SuccessEnvelope<{ members: CompanyMember[] }>>(
      `/admin/companies/${id}/members${qs({ limit })}`,
    );
    return result.data;
  },

  getCompanyTopUsers: async (
    id: string,
    params: { range?: number; limit?: number } = {},
  ): Promise<{ users: CompanyTopUser[] }> => {
    const result = await api.get<SuccessEnvelope<{ users: CompanyTopUser[] }>>(
      `/admin/companies/${id}/top-users${qs(params)}`,
    );
    return result.data;
  },

  // The company's events across every project in the org (cursor-paged).
  getCompanyEvents: async (
    id: string,
    params: { range?: number; limit?: number; cursor?: string } = {},
  ): Promise<{ events: ProjectEvent[]; next_cursor: string | null }> => {
    const result = await api.get<
      SuccessEnvelope<{ events: ProjectEvent[]; next_cursor: string | null }>
    >(`/admin/companies/${id}/events${qs(params)}`);
    return result.data;
  },

  // Manually merge profile fields into a company's attributes. Returns the
  // updated company (same shape as CompanyDetail['company']).
  updateCompanyProfile: async (
    id: string,
    input: UpdateCompanyProfileRequest,
  ): Promise<{ company: CompanyWithLinks }> => {
    const result = await api.put<
      SuccessEnvelope<{ company: CompanyWithLinks }>
    >(`/admin/companies/${id}/profile`, input);
    return result.data;
  },

  enrichCompany: async (id: string): Promise<EnrichResult> => {
    const result = await api.post<SuccessEnvelope<EnrichResult>>(
      `/admin/enrichment/companies/${id}/enrich`,
    );
    return result.data;
  },

  // The identity's primary company (user→company connected view).
  getCompanyForIdentity: async (
    identityId: string,
  ): Promise<{ company: CompanyForIdentity }> => {
    const result = await api.get<
      SuccessEnvelope<{ company: CompanyForIdentity }>
    >(`/admin/identities/${identityId}/company`);
    return result.data;
  },

  // ---- Group reporting (non-company types) ----
  listGroups: async (params: {
    group_type: string;
    range?: number;
    sort?: 'users' | 'events' | 'last_seen';
    limit?: number;
    project_id?: string;
  }): Promise<GroupsListResponse> => {
    const result = await api.get<SuccessEnvelope<GroupsListResponse>>(
      `/admin/groups${qs(params)}`,
    );
    return result.data;
  },

  getGroupOverview: async (params: {
    group_type: string;
    range?: number;
    project_id?: string;
  }): Promise<GroupOverview> => {
    const result = await api.get<SuccessEnvelope<GroupOverview>>(
      `/admin/groups/overview${qs(params)}`,
    );
    return result.data;
  },

  getGroupTopUsers: async (params: {
    group_type: string;
    group_key: string;
    range?: number;
    limit?: number;
    project_id?: string;
  }): Promise<GroupTopUsersResponse> => {
    const result = await api.get<SuccessEnvelope<GroupTopUsersResponse>>(
      `/admin/groups/top-users${qs(params)}`,
    );
    return result.data;
  },

  // ---- Group types ----
  listGroupTypes: async (): Promise<{ group_types: GroupType[] }> => {
    const result =
      await api.get<SuccessEnvelope<{ group_types: GroupType[] }>>(
        '/admin/group-types',
      );
    return result.data;
  },

  createGroupType: async (input: {
    key: string;
    singular_name: string;
    plural_name: string;
    icon?: string;
  }): Promise<{ group_type: GroupType }> => {
    const result = await api.post<SuccessEnvelope<{ group_type: GroupType }>>(
      '/admin/group-types',
      input,
    );
    return result.data;
  },

  updateGroupType: async (
    id: string,
    input: { singular_name?: string; plural_name?: string; icon?: string },
  ): Promise<{ group_type: GroupType }> => {
    const result = await api.put<SuccessEnvelope<{ group_type: GroupType }>>(
      `/admin/group-types/${id}`,
      input,
    );
    return result.data;
  },

  deleteGroupType: async (id: string): Promise<void> => {
    await api.delete(`/admin/group-types/${id}`);
  },

  // ---- Provider config (super-admin) ----
  listProviderConfig: async (
    organizationId?: string,
  ): Promise<ProviderConfigListResponse> => {
    const result = await api.get<SuccessEnvelope<ProviderConfigListResponse>>(
      `/config/provider-config${qs({ organization_id: organizationId })}`,
    );
    return result.data;
  },

  setProviderConfig: async (input: {
    organization_id?: string;
    slot: string;
    providers: string[];
    enabled?: boolean;
  }): Promise<void> => {
    await api.put('/config/provider-config', input);
  },
};
